var _themes;

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/* eslint-disable no-undefined */

import * as React from 'react';
import guid from '../utils/guid';
import styled, { css, keyframes } from 'styled-components';
import { isMobileSafari } from 'react-device-detect';
import tscStyles from '../constants/TscStyles';
import TscThemeName from '../constants/TscThemeName';
import { getClassName } from '../utils/get-class-name';

var glyphDataURI = function glyphDataURI(fill) {
  var glyphFill = fill || tscStyles.color.text.dark;
  var encodedGlyphFill = encodeURIComponent(glyphFill);
  return "data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 16 16'><path fill='".concat(encodedGlyphFill, "' d='M11.78,5.29,8.24,8.83,4.71,5.29a1,1,0,0,0-1.41,0h0a1,1,0,0,0,0,1.41l3.54,3.54h0l1.41,1.41,1.41-1.41,3.54-3.54a1,1,0,0,0,0-1.41h0A1,1,0,0,0,11.78,5.29Z'/></svg>");
};

var themes = (_themes = {}, _defineProperty(_themes, TscThemeName.dawn, {
  border: tscStyles.color.ui.dawn.dark,
  color: tscStyles.color.text.dark,
  labelColor: tscStyles.color.text.mediumDark,
  backgroundColor: tscStyles.color.ui.dawn.light,
  glyphDataURI: glyphDataURI(tscStyles.color.text.dark)
}), _defineProperty(_themes, TscThemeName.dusk, {
  border: tscStyles.color.ui.dusk.lightMedium,
  color: tscStyles.color.text.light,
  labelColor: tscStyles.color.text.light,
  backgroundColor: tscStyles.color.ui.dusk.dark,
  glyphDataURI: glyphDataURI(tscStyles.color.text.light)
}), _themes);
var SelectInputContainer = styled.div.attrs(function (props) {
  return props.testId ? {
    'data-test-id': props.testId
  } : {};
}).withConfig({
  displayName: "SelectInput__SelectInputContainer"
})(["width:", ";"], function (props) {
  return props.prioritizedWidth;
});
var screenReaderOnly = css(["position:absolute;left:-10000px;top:auto;width:1px;height:1px;overflow:hidden;"]);
var focusAnimation = keyframes(["0%{box-shadow:", " 0px 0px 0px 0px}50%{box-shadow:", " 0px 0px 0px 3px}100%{box-shadow:", " 0px 0px 0px 1px}"], tscStyles.color.action.primary, tscStyles.color.action.primary, tscStyles.color.action.primary);
var Select = styled.select.withConfig({
  displayName: "SelectInput__Select"
})(["appearance:none;background-position:center right .5rem;background-image:url(\"", "\");background-repeat:no-repeat;background-color:", ";border:1px solid ", ";color:", ";border-radius:", ";height:2rem;padding:0 1.5rem 0 .5rem;margin:0;display:block;font-size:", ";display:flex;align-items:center;cursor:pointer;box-sizing:border-$box;width:", ";font-family:inherit;&::-ms-expand{display:none;}&:focus{outline:none;border-color:", ";animation:", " 500ms ease;animation-fill-mode:forwards;box-shadow:", " 0px 0px 0px 1px;}"], function (props) {
  return themes[props.themeName].glyphDataURI;
}, function (props) {
  return themes[props.themeName].backgroundColor;
}, function (props) {
  return themes[props.themeName].border;
}, function (props) {
  return themes[props.themeName].color;
}, tscStyles.border.radius.md, isMobileSafari ? '1rem' : '.875rem', function (props) {
  return props.prioritizedWidth;
}, tscStyles.color.action.primary, focusAnimation, tscStyles.color.action.primary);
var Label = styled.label.withConfig({
  displayName: "SelectInput__Label"
})(["display:block;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;line-height:1.4;font-family:inherit;color:", ";font-size:.875rem;font-weight:600;margin-bottom:", ";width:", ";", ""], function (props) {
  return themes[props.themeName].labelColor;
}, tscStyles.space.tiny, function (props) {
  return props.prioritizedWidth;
}, function (props) {
  return props.hideLabel ? screenReaderOnly : '';
});

var SelectInput = function SelectInput(_ref) {
  var className = _ref.className,
      _ref$disabled = _ref.disabled,
      disabled = _ref$disabled === void 0 ? false : _ref$disabled,
      _ref$hideLabel = _ref.hideLabel,
      hideLabel = _ref$hideLabel === void 0 ? false : _ref$hideLabel,
      _ref$isFullWidth = _ref.isFullWidth,
      isFullWidth = _ref$isFullWidth === void 0 ? true : _ref$isFullWidth,
      _ref$label = _ref.label,
      label = _ref$label === void 0 ? '' : _ref$label,
      onChange = _ref.onChange,
      _ref$options = _ref.options,
      options = _ref$options === void 0 ? [] : _ref$options,
      testId = _ref.testId,
      _ref$themeName = _ref.themeName,
      themeName = _ref$themeName === void 0 ? TscThemeName.dawn : _ref$themeName,
      _ref$value = _ref.value,
      value = _ref$value === void 0 ? '' : _ref$value,
      width = _ref.width;
  // generate this once
  var internalId = React.useMemo(function () {
    return "select-input-".concat(guid.new());
  }, []);
  var controlProps = {
    themeName: themeName,
    hideLabel: hideLabel,
    prioritizedWidth: isFullWidth ? '100%' : width || 'auto'
  };
  return _jsxs(SelectInputContainer, _objectSpread(_objectSpread({
    className: getClassName(className, null, 'select-wrapper')
  }, controlProps), {}, {
    testId: testId,
    children: [!!label.length && _jsx(Label, _objectSpread(_objectSpread({
      className: getClassName(className, 'label'),
      htmlFor: internalId
    }, controlProps), {}, {
      hideLabel: hideLabel,
      children: label
    })), _jsx(Select, _objectSpread(_objectSpread({
      className: getClassName(className, 'select')
    }, controlProps), {}, {
      onChange: onChange,
      value: value || undefined,
      id: internalId,
      disabled: disabled,
      children: options.map(function (o) {
        return _jsx("option", {
          value: o.value !== undefined ? o.value : o.label,
          label: o.label,
          disabled: o.disabled,
          children: o.label
        }, o.value !== undefined ? o.value : o.label);
      })
    }))]
  }));
};

var SelectInputTest = {
  Select: Select
};
export { SelectInput, SelectInputTest };
export default SelectInput;